/**
 * Shared Telematics Availability Score — Control Centre + Telematics tab.
 *
 * Measures GPS coverage quality for vehicles that are currently in use
 * (moving + idle + stopped). Offline units are excluded so spare/parked-off
 * fleet does not drag the operational coverage score.
 *
 * Score = 100
 *   − 2 × (alerts / in-use vehicles)
 *   − 1 × (missing-GPS % among in-use)
 */

export const TELEMATICS_KPI_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

export type TelematicsAvailabilityInputs = {
  movingVehicles: number;
  idleVehicles: number;
  stoppedVehicles: number;
  alertCount: number;
  /** In-use vehicles that currently have a usable lat/lng (not offline). */
  vehiclesWithGps: number;
  /** Provider position ping count; 0 forces a full missing-GPS penalty. */
  positionCount: number;
};

export type TelematicsVehicleLike = {
  status?: string | null;
  lastUpdate?: string | null;
  LocalTime?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  lat?: number | null;
  lng?: number | null;
};

export type TelematicsAvailabilitySummary = TelematicsAvailabilityInputs & {
  totalVehicles: number;
  offlineVehicles: number;
  availabilityScore: number;
  /**
   * False when there are no in-use vehicles to score (fleet parked, or an
   * aggregate payload with no moving/idle/stopped breakdown). `availabilityScore`
   * is 0 in that case as a sentinel — callers should render "no data", not a
   * red 0%.
   */
  scorable: boolean;
};

export function telematicsInUseCount(input: Pick<
  TelematicsAvailabilityInputs,
  'movingVehicles' | 'idleVehicles' | 'stoppedVehicles'
>): number {
  return Math.max(
    0,
    (Number(input.movingVehicles) || 0)
      + (Number(input.idleVehicles) || 0)
      + (Number(input.stoppedVehicles) || 0),
  );
}

export function telematicsVehicleStatus(v: { status?: string | null }): string {
  return String(v.status || '').trim().toLowerCase();
}

export function telematicsHasUsableGps(v: {
  latitude?: number | null;
  longitude?: number | null;
  lat?: number | null;
  lng?: number | null;
}): boolean {
  const lat = v.latitude ?? v.lat;
  const lng = v.longitude ?? v.lng;
  return lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0;
}

export function telematicsIsInUseStatus(status: string): boolean {
  return status === 'moving' || status === 'idle' || status === 'stopped' || status === 'parked';
}

/** Vehicles with a recent enough lastUpdate (or LocalTime) for KPI scoring. */
export function filterTelematicsKpiEligible<T extends TelematicsVehicleLike>(
  vehicles: readonly T[],
  opts?: { nowMs?: number; maxAgeMs?: number },
): T[] {
  const nowMs = opts?.nowMs ?? Date.now();
  const maxAgeMs = opts?.maxAgeMs ?? TELEMATICS_KPI_MAX_AGE_MS;
  return vehicles.filter((v) => {
    const ts = v.lastUpdate ?? v.LocalTime;
    if (!ts) return false;
    const ageMs = nowMs - new Date(ts).getTime();
    return Number.isFinite(ageMs) && ageMs >= 0 && ageMs < maxAgeMs;
  });
}

/**
 * Summarise a vehicle list for Availability Score.
 * `vehiclesWithGps` counts only in-use units with usable coordinates.
 */
export function summarizeTelematicsVehicles(
  vehicles: readonly TelematicsVehicleLike[],
  opts?: { alertCount?: number; positionCount?: number; nowMs?: number; maxAgeMs?: number },
): TelematicsAvailabilitySummary {
  const eligible = filterTelematicsKpiEligible(vehicles, opts);
  let movingVehicles = 0;
  let idleVehicles = 0;
  let stoppedVehicles = 0;
  let offlineVehicles = 0;
  let vehiclesWithGps = 0;

  for (const v of eligible) {
    const status = telematicsVehicleStatus(v);
    if (status === 'moving') movingVehicles += 1;
    else if (status === 'idle') idleVehicles += 1;
    else if (status === 'stopped' || status === 'parked') stoppedVehicles += 1;
    else if (status === 'offline') offlineVehicles += 1;

    if (telematicsIsInUseStatus(status) && telematicsHasUsableGps(v)) {
      vehiclesWithGps += 1;
    }
  }

  const alertCount = Math.max(0, Number(opts?.alertCount) || 0);
  const positionCount = Math.max(0, Number(opts?.positionCount) || 0);
  const inputs: TelematicsAvailabilityInputs = {
    movingVehicles,
    idleVehicles,
    stoppedVehicles,
    alertCount,
    vehiclesWithGps,
    positionCount,
  };

  return {
    ...inputs,
    totalVehicles: eligible.length,
    offlineVehicles,
    availabilityScore: telematicsAvailabilityScore(inputs),
    scorable: telematicsInUseCount(inputs) > 0,
  };
}

/**
 * Aggregate-only fallback when no vehicle list is available.
 * Without coords we assume in-use units have GPS iff positionCount > 0.
 */
export function summarizeTelematicsAggregates(input: {
  totalVehicles?: number;
  movingVehicles?: number;
  idleVehicles?: number;
  stoppedVehicles?: number;
  offlineVehicles?: number;
  alertCount?: number;
  positionCount?: number;
}): TelematicsAvailabilitySummary {
  const movingVehicles = Math.max(0, Number(input.movingVehicles) || 0);
  const idleVehicles = Math.max(0, Number(input.idleVehicles) || 0);
  const stoppedVehicles = Math.max(0, Number(input.stoppedVehicles) || 0);
  const offlineVehicles = Math.max(0, Number(input.offlineVehicles) || 0);
  const trackable = movingVehicles + idleVehicles + stoppedVehicles;
  const positionCount = Math.max(0, Number(input.positionCount) || 0);
  const alertCount = Math.max(0, Number(input.alertCount) || 0);
  const vehiclesWithGps = positionCount > 0 ? trackable : 0;
  const inputs: TelematicsAvailabilityInputs = {
    movingVehicles,
    idleVehicles,
    stoppedVehicles,
    alertCount,
    vehiclesWithGps,
    positionCount,
  };
  const totalVehicles =
    Math.max(0, Number(input.totalVehicles) || 0)
    || trackable + offlineVehicles;

  return {
    ...inputs,
    totalVehicles,
    offlineVehicles,
    availabilityScore: telematicsAvailabilityScore(inputs),
    scorable: trackable > 0,
  };
}

export function telematicsAvailabilityScore(input: TelematicsAvailabilityInputs): number {
  const trackableCount = telematicsInUseCount(input);
  if (trackableCount <= 0) return 0;

  const alertCount = Math.max(0, Number(input.alertCount) || 0);
  const vehiclesWithGps = Math.max(0, Number(input.vehiclesWithGps) || 0);
  const positionCount = Math.max(0, Number(input.positionCount) || 0);

  const alertPenalty = 2.0;
  const gpsCoveragePenalty = 1.0;

  const missingGPSRateTrackable = Math.max(
    0,
    ((trackableCount - vehiclesWithGps) / trackableCount) * 100,
  );
  const missingGPSRate = positionCount === 0 ? 100 : missingGPSRateTrackable;
  const alertsPerTrackableVehicle = alertCount / trackableCount;

  const score =
    100
    - alertPenalty * alertsPerTrackableVehicle
    - gpsCoveragePenalty * missingGPSRate;

  return Math.max(0, Math.min(100, score));
}

export function telematicsAvailabilityLabel(score: number): string {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  return 'Needs Attention';
}

export const TELEMATICS_AVAILABILITY_FORMULA =
  'Score = 100 − (alerts ÷ in-use × 2) − (missing GPS % among in-use × 1). '
  + 'In-use = moving + idle + stopped. Offline vehicles are excluded.';
